import { useState, useEffect } from "react";
import { AuthAPI } from "../../api/AuthAPI";
import { login } from "../../utils/auth";
import { useCart } from "../../context/CartContext";

const STORAGE_KEY = "login_form_draft";

const validators = {
  email: (v) =>
    !v.trim()
      ? "Email is required"
      : !/\S+@\S+\.\S+/.test(v)
        ? "Enter a valid email address"
        : "",
  password: (v) => (!v ? "Password is required" : ""),
};

export default function EmailLoginForm({ onSuccess }) {
  const [form, setForm] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) return { ...JSON.parse(saved), password: "" }; // never restore password
    } catch (_) {}
    return { email: "", password: "" };
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState("");
  const [serverError, setServerError] = useState("");
  const { syncGuestCartAfterLogin } = useCart();

  /* persist email only */
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ email: form.email }));
  }, [form.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setServerError("");
    if (touched[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validators[name]?.(value) ?? "",
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setFocused("");
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validators[name]?.(value) ?? "" }));
  };

  const handleSubmit = async () => {
    const allTouched = { email: true, password: true };
    setTouched(allTouched);
    const errs = {
      email: validators.email(form.email),
      password: validators.password(form.password),
    };
    setErrors(errs);
    if (errs.email || errs.password) return;

    try {
      setLoading(true);
      setServerError("");
      const res = await AuthAPI.login(form);
      login(res.data);
      await syncGuestCartAfterLogin();
      sessionStorage.removeItem(STORAGE_KEY);
      onSuccess();
    } catch (err) {
      setServerError(
        err.response?.data?.message || "Incorrect email or password.",
      );
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    {
      name: "email",
      label: "Email address",
      placeholder: "you@example.com",
      type: "email",
    },
    {
      name: "password",
      label: "Password",
      placeholder: "••••••••",
      type: "password",
    },
  ];

  return (
    <div>
      {fields.map(({ name, label, placeholder, type }) => {
        const err = touched[name] && errors[name];
        return (
          <div key={name} style={{ marginBottom: "12px" }}>
            <label style={labelStyle}>{label}</label>
            <input
              type={type}
              name={name}
              placeholder={placeholder}
              value={form[name]}
              onChange={handleChange}
              onFocus={() => setFocused(name)}
              onBlur={handleBlur}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              style={inputStyle(focused === name, !!err)}
            />
            {err && (
              <p style={fieldErrorStyle}>
                <ErrIcon /> {err}
              </p>
            )}
          </div>
        );
      })}

      {serverError && (
        <div style={serverErrBoxStyle}>
          <ErrIcon size={14} /> {serverError}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={loading}
        style={btnStyle(loading)}
      >
        {loading ? (
          <>
            <Spinner /> Signing in…
          </>
        ) : (
          "Sign in"
        )}
      </button>
    </div>
  );
}

function ErrIcon({ size = 12 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function Spinner() {
  return (
    <span
      style={{
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,0.35)",
        borderTopColor: "#fff",
        borderRadius: "50%",
        display: "inline-block",
        animation: "spin 0.7s linear infinite",
        marginRight: 8,
      }}
    />
  );
}

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 500,
  color: "#374151",
  marginBottom: "6px",
  letterSpacing: "0.02em",
};

const inputStyle = (focused, hasErr) => ({
  width: "100%",
  height: "42px",
  padding: "0 14px",
  border: `1.5px solid ${hasErr ? "#fca5a5" : focused ? "#2563eb" : "#e5e7eb"}`,
  borderRadius: "10px",
  fontSize: "14px",
  color: "#111827",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.15s",
  background: hasErr ? "#fff8f8" : "#fafafa",
});

const fieldErrorStyle = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
  fontSize: "11.5px",
  color: "#dc2626",
  marginTop: "5px",
};

const serverErrBoxStyle = {
  background: "#fef2f2",
  border: "1px solid #fecaca",
  borderRadius: "8px",
  padding: "10px 12px",
  fontSize: "13px",
  color: "#dc2626",
  marginTop: "4px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const btnStyle = (loading) => ({
  width: "100%",
  height: "42px",
  marginTop: "18px",
  borderRadius: "10px",
  border: "none",
  background: loading ? "#93c5fd" : "#2563eb",
  color: "#fff",
  fontSize: "14px",
  fontWeight: 500,
  cursor: loading ? "not-allowed" : "pointer",
  transition: "background 0.15s",
  letterSpacing: "0.01em",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});
